import { claude, requireKey } from '../api.js';
import { ACTION_OPTS, BQ, GAP_SYS, PRICE_OPTS, VIBES } from '../constants.js';
import { $, briefTxt, dv, mk, mkUpload, renderStepBar, showErr } from '../dom.js';
import {
  assembleEnrichment as _assembleEnrichment,
  calcCompleteness as _calcCompleteness,
} from '../logic/brief.ts';
import { GAP_ANALYSIS_SCHEMA } from '../prompts/constants.js';
import { ST } from '../state.js';

// ── Q&A BRIEF FLOW ───────────────────────────────────────────────
// Question-based onboarding, gap analysis, follow-ups, summary.

// ── DOM RENDERING (becomes Vue <template> during migration) ──
// renderBrief(), renderQDots(), renderQ(), renderFollowups(), renderSummary()

/**
 * Render the brief screen — step bar, progress dots, and current question card.
 */
function renderBrief() {
  renderStepBar('brief-sb', 'brief');
  renderQDots();
  renderQ();
}

/** Render the step progress dots for the brief flow. */
function renderQDots() {
  const c = $('brief-qdots');
  if (!c) {
    return;
  }
  c.className = 'qdots';
  c.innerHTML = '';
  for (let i = 0; i < 4; i++) {
    c.append(
      mk('div', {
        class:
          'qdot ' +
          (i < ST.briefStep ? 'qdot-done' : i === ST.briefStep ? 'qdot-active' : 'qdot-idle'),
      }),
    );
  }
}

/** Render the current question card (step 0-3) with inputs and navigation. */
function renderQ() {
  const card = $('brief-qcard');
  if (!card) {
    return;
  }
  card.innerHTML = '';
  const q = BQ[ST.briefStep],
    b = ST.brief;

  const tr = dv('margin-bottom:11px');
  tr.append(
    mk('span', { class: 'qtag' }, q.tag),
    mk('span', { class: 'qunlocks' }, ` ${q.unlocks}`),
  );
  card.append(tr);

  if (ST.briefStep === 0) {
    card.append(
      mk(
        'div',
        { class: 'qhead' },
        'What does your business do? Describe it like you would at a dinner party.',
      ),
    );
    const ta = mk('textarea', {
      class: 'inp',
      rows: '4',
      placeholder:
        'e.g. We install and repair garage doors for homeowners in the Seattle area. 15 years, same-day emergency service.',
      style: 'min-height:100px',
    });
    ta.value = b.businessDesc || '';
    ta.addEventListener('input', e => {
      ST.brief.businessDesc = e.target.value;
      updNext();
    });
    card.append(ta);
    card.append(
      mk(
        'div',
        { style: 'font-size:13px;color:var(--txt3);margin-top:8px' },
        'The more detail, the better your site.',
      ),
    );
  } else if (ST.briefStep === 1) {
    card.append(
      mk(
        'div',
        { class: 'qhead' },
        'Who is your ideal customer, and what problem are they trying to solve?',
      ),
    );
    const ta = mk('textarea', {
      class: 'inp',
      rows: '4',
      placeholder:
        "e.g. Homeowners whose garage door broke. They can't get their car out, they're comparing 2-3 companies on price and speed.",
      style: 'min-height:100px',
    });
    ta.value = b.customerProblem || '';
    ta.addEventListener('input', e => {
      ST.brief.customerProblem = e.target.value;
      updNext();
    });
    card.append(ta);
  } else if (ST.briefStep === 2) {
    card.append(
      mk(
        'div',
        { class: 'qhead' },
        'When someone visits your site, what is the ONE thing you want them to do?',
      ),
    );
    const grid = mk('div', { class: 'act-grid' });
    ACTION_OPTS.forEach(opt => {
      const sel = b.desiredAction === opt.id;
      const row = mk('div', {
        class: `act-opt${sel ? ' selected' : ''}`,
        onclick() {
          ST.brief.desiredAction = opt.id;
          renderQ();
          updNext();
        },
      });
      row.append(mk('div', { class: 'act-radio' }), mk('div', { class: 'act-label' }, opt.label));
      grid.append(row);
    });
    card.append(grid);
    // Conditional: price for buy
    const cp = mk('div', { class: `cond-wrap${b.desiredAction === 'buy' ? ' open' : ''}` });
    cp.append(
      mk(
        'div',
        { style: 'font-size:13px;font-weight:600;color:var(--txt2);margin-bottom:8px' },
        'Approximate price point:',
      ),
    );
    const pg = mk('div', { class: 'act-grid' });
    PRICE_OPTS.forEach(p => {
      const s2 = b.actionDetail === p;
      const r2 = mk('div', {
        class: `act-opt${s2 ? ' selected' : ''}`,
        style: 'padding:10px 14px',
        onclick() {
          ST.brief.actionDetail = p;
          renderQ();
        },
      });
      r2.append(mk('div', { class: 'act-radio' }), mk('div', { class: 'act-label' }, p));
      pg.append(r2);
    });
    cp.append(pg);
    card.append(cp);
    // Conditional: lead magnet for non-buy/non-other
    const sl = b.desiredAction && b.desiredAction !== 'buy' && b.desiredAction !== 'other';
    const cl = mk('div', { class: `cond-wrap${sl ? ' open' : ''}` });
    cl.append(
      mk(
        'div',
        { style: 'font-size:13px;font-weight:600;color:var(--txt2);margin-bottom:8px' },
        'Anything to get the conversation started?',
      ),
    );
    const li = mk('input', {
      class: 'inp',
      type: 'text',
      placeholder: 'Free estimate, guide, discount...',
      value: b.leadMagnet || '',
    });
    li.addEventListener('input', e => {
      ST.brief.leadMagnet = e.target.value;
    });
    cl.append(li);
    cl.append(mk('div', { style: 'font-size:12px;color:var(--txt3);margin-top:6px' }, 'Optional.'));
    card.append(cl);
    // Conditional: custom action for other
    const co = mk('div', { class: `cond-wrap${b.desiredAction === 'other' ? ' open' : ''}` });
    co.append(
      mk(
        'div',
        { style: 'font-size:13px;font-weight:600;color:var(--txt2);margin-bottom:8px' },
        'Describe the action:',
      ),
    );
    const oi = mk('input', {
      class: 'inp',
      type: 'text',
      placeholder: 'Download app, join community...',
      value: b.desiredAction === 'other' ? b.actionDetail || '' : '',
    });
    oi.addEventListener('input', e => {
      if (b.desiredAction === 'other') {
        ST.brief.actionDetail = e.target.value;
      }
    });
    co.append(oi);
    card.append(co);
  } else {
    card.append(mk('div', { class: 'qhead' }, 'Pick your brand personality (up to 2):'));
    const g = mk('div', { class: 'vgrid' });
    VIBES.forEach(v => {
      const sel = b.vibes.includes(v.id);
      const vc = mk('div', {
        class: `vcard${sel ? ' sel' : ''}`,
        onclick() {
          let c2 = [].concat(ST.brief.vibes);
          if (c2.includes(v.id)) {
            c2 = c2.filter(x => x !== v.id);
          } else if (c2.length < 2) {
            c2.push(v.id);
          } else {
            c2.shift();
            c2.push(v.id);
          }
          ST.brief.vibes = c2;
          renderQ();
          updNext();
        },
      });
      const sr = mk('div', { class: 'vsw-row' });
      v.colors.forEach(c3 => {
        sr.append(mk('div', { class: 'vsw', style: `background:${c3}` }));
      });
      vc.append(sr, mk('div', { class: 'vname' }, v.name), mk('div', { class: 'vsub' }, v.sub));
      g.append(vc);
    });
    card.append(g);
    // ref site
    const rr = dv('margin-bottom:12px');
    rr.append(
      mk(
        'div',
        { style: 'font-size:11px;font-weight:500;color:var(--txt2);margin-bottom:6px' },
        'Sites whose look you love (optional):',
      ),
    );
    const ri = mk('input', {
      class: 'inp',
      type: 'text',
      placeholder: 'stripe.com, linear.app…',
      value: b.refsite || '',
    });
    ri.addEventListener('input', e => {
      ST.brief.refsite = e.target.value;
    });
    rr.append(ri);
    card.append(rr);
    // logo (optional)
    const lu = dv('');
    lu.append(
      mk(
        'div',
        { style: 'font-size:11px;font-weight:500;color:var(--txt2);margin-bottom:6px' },
        'Upload your logo (optional):',
      ),
    );
    lu.append(
      mkUpload(
        'Drop logo or brand image',
        'PNG, JPG',
        ST.logoImg,
        img => {
          ST.logoImg = img;
          renderQ();
        },
        () => {
          ST.logoImg = null;
          renderQ();
        },
      ),
    );
    card.append(lu);
  }

  if (q.ex) {
    card.append(mk('div', { class: 'qex' }, q.ex));
  }
  card.append(mk('div', { class: 'qwhy' }, `Why: ${q.why}`));

  const nav = mk('div', { class: 'qnav' });
  const bk = mk(
    'button',
    { class: 'btn btn-g', style: ST.briefStep === 0 ? 'opacity:.3' : '' },
    '← Back',
  );
  bk.disabled = ST.briefStep === 0;
  bk.onclick = () => {
    if (ST.briefStep > 0) {
      ST.briefStep--;
      renderBrief();
    }
  };
  const isLast = ST.briefStep === 3;
  const nx = mk(
    'button',
    { class: 'btn btn-p', id: 'brief-next' },
    isLast ? 'Analyze my brief →' : 'Next →',
  );
  nx.onclick = () => {
    if (isLast) {
      runGapAnalysis();
    } else {
      ST.briefStep++;
      renderBrief();
    }
  };
  nav.append(bk, nx);
  card.append(nav);
  updNext();
}

/**
 * Enable/disable the "Next" button based on current step's validation.
 * Steps 0-1 require non-empty text, step 2 requires a selected action,
 * step 3 requires at least one vibe.
 */
function updNext() {
  const btn = $('brief-next');
  if (!btn) {
    return;
  }
  const b = ST.brief;
  let can = true;
  if (ST.briefStep === 0) {
    can = !!(b.businessDesc && b.businessDesc.trim());
  } else if (ST.briefStep === 1) {
    can = !!(b.customerProblem && b.customerProblem.trim());
  } else if (ST.briefStep === 2) {
    can = !!b.desiredAction;
  } else if (ST.briefStep === 3) {
    can = b.vibes.length > 0;
  }
  btn.disabled = !can;
}

// ── API CALLS (extract to composable during Vue migration) ──
// runGapAnalysis() — Claude API call + state update

/**
 * Send the brief to Claude for gap analysis, then navigate to follow-ups.
 * Falls back to hardcoded questions on parse/network errors.
 */
function runGapAnalysis() {
  requireKey(() => {
    G.go('loading');
    const ldTitle = $('ld-dna-title');
    if (ldTitle) {
      ldTitle.textContent = 'Analyzing your brief';
    }
    const ldSub = $('ld-dna-sub');
    if (ldSub) {
      ldSub.textContent = 'Identifying gaps…';
    }
    claude({
      sys: GAP_SYS,
      user: briefTxt(),
      tokens: 800,
      effort: 'low',
      schema: GAP_ANALYSIS_SCHEMA,
    })
      .then(raw => {
        try {
          const parsed = JSON.parse(raw);
          const cm = {
            DIFFERENTIATION: 'diff',
            PROOF: 'proof',
            OBJECTION: 'objection',
            TRANSFORMATION: 'transform',
            PROCESS: 'process',
            LOCAL: 'local',
          };
          ST.followUps = (parsed.questions || []).map(g2 => ({
            category: g2.category,
            catClass: cm[g2.category] || 'diff',
            question: g2.question,
            answer: '',
            skipped: false,
          }));
        } catch {
          ST.followUps = [
            {
              category: 'DIFFERENTIATION',
              catClass: 'diff',
              question: 'What do customers say is why they chose you?',
              fallback: 'AI generates generic differentiators.',
              answer: '',
              skipped: false,
            },
            {
              category: 'PROOF',
              catClass: 'proof',
              question: 'How long in business and how many customers?',
              fallback: 'AI uses "experienced provider".',
              answer: '',
              skipped: false,
            },
          ];
        }
        G.go('followups');
      })
      .catch(er => {
        showErr(er.message);
        ST.followUps = [
          {
            category: 'DIFFERENTIATION',
            catClass: 'diff',
            question: 'What do customers say is why they chose you?',
            fallback: 'AI generates generic differentiators.',
            answer: '',
            skipped: false,
          },
          {
            category: 'PROOF',
            catClass: 'proof',
            question: 'How long in business?',
            fallback: 'AI uses "experienced provider".',
            answer: '',
            skipped: false,
          },
        ];
        G.go('followups');
      });
  });
}

/** Render follow-up question cards with skip toggles and answer textareas. */
function renderFollowups() {
  renderStepBar('fu-sb', 'brief');
  const c = $('fu-cards');
  if (!c) {
    return;
  }
  c.innerHTML = '';
  ST.followUps.forEach((fu, i) => {
    const card = mk('div', { class: 'fu-card' + (fu.skipped ? ' skipped' : ''), id: 'fu-' + i });
    const top = mk('div', { class: 'fu-top' });
    top.append(mk('span', { class: 'fu-cat fu-cat-' + fu.catClass }, fu.category));
    const sb = mk(
      'button',
      {
        class: 'fu-skip' + (fu.skipped ? ' active' : ''),
        onclick() {
          ST.followUps[i].skipped = !ST.followUps[i].skipped;
          renderFollowups();
        },
      },
      fu.skipped ? 'Unskip' : 'Skip',
    );
    top.append(sb);
    card.append(top);
    card.append(mk('div', { class: 'fu-question' }, fu.question));
    const ta = mk('textarea', {
      class: 'inp',
      rows: '2',
      placeholder: 'Your answer…',
      style: 'min-height:56px;font-size:14px',
    });
    ta.value = fu.answer || '';
    ta.disabled = fu.skipped;
    ta.addEventListener('input', e => {
      ST.followUps[i].answer = e.target.value;
    });
    card.append(ta);
    card.append(mk('div', { class: 'fu-fallback' }, 'If skipped: ' + fu.fallback));
    c.append(card);
  });
}

/** Render the brief summary screen — business context, enrichment, completeness bar. */
function renderSummary() {
  renderStepBar('sum-sb', 'brief');
  assembleEnrichment();

  const bc = $('sum-business');
  if (bc) {
    bc.innerHTML = '';
    bc.append(mk('div', { class: 'sum-title' }, 'Business Context'));
    const b = ST.brief;
    const vn = VIBES.filter(v => b.vibes.includes(v.id)).map(v => v.name);
    let al = '';
    ACTION_OPTS.forEach(a => {
      if (a.id === b.desiredAction) {
        al = a.label;
      }
    });
    const rows = [
      ['Business', b.businessDesc || ''],
      ['Customer', b.customerProblem || ''],
      ['CTA', al + (b.actionDetail ? ' / ' + b.actionDetail : '')],
      ['Visual', vn.join(', ') || '-'],
    ];
    if (b.leadMagnet) {
      rows.splice(3, 0, ['Lead magnet', b.leadMagnet]);
    }
    rows.forEach(r => {
      const row = mk('div', { class: 'sum-row' });
      row.append(mk('div', { class: 'sum-key' }, r[0]), mk('div', { class: 'sum-val' }, r[1]));
      bc.append(row);
    });
  }

  const ec = $('sum-enrichment');
  if (ec) {
    ec.innerHTML = '';
    ec.append(mk('div', { class: 'sum-title' }, 'Conversion Intelligence'));
    const e = ST.enrichment || {};
    const cm2 = {
      differentiation: 'Differentiation',
      proof: 'Proof',
      objections: 'Objections',
      transformation: 'Transformation',
      process: 'Process',
      localContext: 'Service area',
    };
    Object.keys(cm2).forEach(key => {
      const val = e[key];
      if (!val) {
        return;
      }
      const row = mk('div', { class: 'sum-row' });
      row.append(mk('div', { class: 'sum-key' }, cm2[key]));
      const ve = mk('div', { class: 'sum-val' }, val);
      if (e.sources && e.sources[key] === 'fallback') {
        ve.append(mk('span', { class: 'sum-assumed' }, 'AI assumed'));
      }
      row.append(ve);
      ec.append(row);
    });
  }

  const cc = $('sum-completeness');
  if (cc) {
    cc.innerHTML = '';
    cc.append(mk('div', { class: 'sum-title' }, 'Brief Completeness'));
    const score = calcCompleteness();
    const bc2 = score >= 80 ? 'sum-bar-hi' : score >= 50 ? 'sum-bar-med' : 'sum-bar-lo';
    const bg2 = mk('div', { class: 'sum-bar-bg' });
    const bf = mk('div', { class: 'sum-bar-fill ' + bc2, id: 'sum-bar-fill', style: 'width:0%' });
    bg2.append(bf);
    cc.append(bg2);
    const mt = mk('div', { class: 'sum-bar-meta' });
    const lt = score >= 80 ? 'Strong foundation' : score >= 50 ? 'Good start' : 'Minimal brief';
    mt.append(
      mk('span', { class: 'sum-bar-label' }, lt),
      mk('span', { class: 'sum-bar-score' }, score + '%'),
    );
    cc.append(mt);
    setTimeout(() => {
      bf.style.width = score + '%';
    }, 100);
  }
}

// ── PURE BUSINESS LOGIC (delegated to logic/brief.ts) ──────────
// assembleEnrichment(), calcCompleteness() — imported from logic/brief.ts
// These wrappers apply the pure functions to ST state.

/** Assemble enrichment from ST.followUps and store in ST.enrichment. */
function assembleEnrichment() {
  ST.enrichment = _assembleEnrichment(ST.followUps);
}

/** Calculate brief completeness percentage from current state. */
function calcCompleteness() {
  return _calcCompleteness(ST.brief, ST.enrichment);
}

// ── CONCEPT GENERATION ────────────────────────────────────────────
/** Kick off concept generation by running gap analysis first. */
function startConcept() {
  runGapAnalysis();
}

// ── MODULE EXPORTS (activate when switching to ES modules) ──────
export {
  assembleEnrichment,
  calcCompleteness,
  renderBrief,
  renderFollowups,
  renderQ,
  renderQDots,
  renderSummary,
  runGapAnalysis,
  startConcept,
  updNext,
};
